import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { CircularProgress } from '@mui/material';
import { PillButton } from '../../../components/superAdminUi';
import WizardShell from '../../../components/common/WizardShell';
import GroupDuplicateWatch from '../../../components/common/GroupDuplicateWatch';
import { loaderAction } from '../../../store/loaderStore';
import matGroupService from '../../../services/api/matGroupService';
import lookupService from '../../../services/api/lookupService';
import { extractServerErrors } from '../../../utils/serverErrors';
import { buildUpdateMatGroupPayload, groupInitialValues } from './payload';
import GroupDetailsStep from './GroupDetailsStep';
import ReviewStep from './ReviewStep';
import SummaryRail from './SummaryRail';

const STEPS = ['Group details', 'Review & save'];

// Full-page group-edit wizard — the same fields EditGroupModal edited, saved through the
// same updateMatGroup payload, now with shared wizard chrome and a real review step.
function EditGroup(props) {
  const params = new URLSearchParams(props.location.search);
  const matId = parseInt(params.get('matId'), 10);

  const [activeStep, setActiveStep] = useState(0);
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [storageLocations, setStorageLocations] = useState([]);
  const [duplicate, setDuplicate] = useState(null);
  const [serverErrors, setServerErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    lookupService.getDataStorageLocations().then((res) => {
      const payload = res?.data ?? res;
      const raw = payload?.locations ?? payload?.Locations;
      if (Array.isArray(raw) && raw.length) {
        setStorageLocations(raw.map((o) => ({ value: o.value ?? o.Value, label: o.label ?? o.Label })));
      }
    });
  }, []);

  useEffect(() => {
    if (!matId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    matGroupService.getMatById(matId).then((response) => {
      if (response && response.data) {
        setGroup(response.data);
      } else {
        toast.error('Failed to load group details');
      }
      setLoading(false);
    }).catch((error) => {
      toast.error('Error loading group details: ' + (error.message || 'Unknown error'));
      setLoading(false);
    });
  }, [matId]);

  const groupId = group ? (group.Id ?? group.id) : matId;

  const save = (formik) => {
    if (duplicate && duplicate.isDuplicate) {
      toast.error('A group with this name already exists.');
      return;
    }
    setServerErrors([]);
    setSubmitting(true);
    const payload = buildUpdateMatGroupPayload(formik.values, groupId);
    props.loaderActions.dataLoading(true);
    matGroupService.updateMatGroup(payload).then((response) => {
      props.loaderActions.dataLoading(false);
      setSubmitting(false);
      const { success, message } = response || {};
      if (success) {
        toast.success('Group has been updated successfully!');
        setSaved(true);
      } else {
        const errors = extractServerErrors(response);
        if (errors.length) {
          setServerErrors(errors.map((e) => e.message));
        } else {
          toast.error(message || 'Failed to update group');
        }
      }
    }).catch(() => {
      props.loaderActions.dataLoading(false);
      setSubmitting(false);
      setServerErrors(['Failed to update group.']);
    });
  };

  const goToGroup = () => props.history.push(`/group-detail?matId=${matId}`);

  if (loading) {
    return (
      <WizardShell title="Edit Group" steps={STEPS} activeStep={0} hideFooter summary={null}>
        <CircularProgress />
      </WizardShell>
    );
  }

  if (!group) {
    return (
      <WizardShell title="Edit Group" steps={STEPS} activeStep={0} hideFooter summary={null}>
        <div>Group not found.</div>
      </WizardShell>
    );
  }

  const blocked = Boolean(duplicate && duplicate.isDuplicate);

  return (
    <Formik
      initialValues={groupInitialValues(group)}
      onSubmit={() => {}}
      validateOnBlur={false}
      validateOnChange
      validationSchema={Yup.object().shape({
        name: Yup.string().trim().max(100, 'Group name must be at most 100 characters.').required('Group name is required.'),
      })}
    >
      {(formik) => {
        const finalAction = submitting
          ? <CircularProgress size={32} />
          : (
            <PillButton type="button" $variant="danger" $size="lg" onClick={() => save(formik)} disabled={blocked}>
              Save changes
            </PillButton>
          );

        const canLeaveStep = () => {
          if (activeStep === 0) {
            if (!formik.values.name || formik.values.name.trim() === '') {
              toast.error('Please enter a group name.');
              return false;
            }
            if (blocked) {
              toast.error('A group with this name already exists.');
              return false;
            }
          }
          return true;
        };

        return (
          <div>
            <GroupDuplicateWatch name={formik.values.name} groupUid="" excludeId={groupId} onResult={setDuplicate} />
            <WizardShell
              title="Edit Group"
              steps={STEPS}
              activeStep={activeStep}
              onStepSelect={(index) => setActiveStep(index)}
              hideFooter={saved}
              onBack={() => setActiveStep((s) => s - 1)}
              onNext={() => { if (canLeaveStep()) setActiveStep((s) => s + 1); }}
              finalAction={finalAction}
              summary={<SummaryRail values={formik.values} blocked={blocked} />}
            >
              {activeStep === 0 && (
                <GroupDetailsStep
                  formik={formik}
                  storageLocations={storageLocations}
                  duplicate={duplicate}
                  serverErrors={serverErrors}
                  onDismissError={(index) => setServerErrors((prev) => prev.filter((_, i) => i !== index))}
                  onGroupFieldEdit={() => setServerErrors([])}
                  onEditSubscription={() => props.history.push(`/edit-subscription?matId=${matId}`)}
                />
              )}
              {activeStep === 1 && (
                <ReviewStep values={formik.values} saved={saved} onGoToGroup={goToGroup} />
              )}
            </WizardShell>
          </div>
        );
      }}
    </Formik>
  );
}

const mapDispatchToProps = (dispatch) => ({
  loaderActions: bindActionCreators(loaderAction, dispatch),
});

export default connect(null, mapDispatchToProps)(withRouter(EditGroup));
